import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/App';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Lock, Eye, Download } from 'lucide-react';

const auth = (t) => ({ headers: { Authorization: `Bearer ${t}` } });

// Shared Building Intelligence report dialog — audience and language are chosen
// independently; Preview and Download always use the SAME audience, language and
// (via onBeforeOpen) the latest saved content. defaultLang seeds the language from
// the form the user is working in (English form → English report, Spanish → Spanish);
// the user can still switch the output language here.
const BiReportDialog = ({ token, profile, onClose, defaultLang = 'en', onBeforeOpen }) => {
  const [audience, setAudience] = useState('client');
  const [lang, setLang] = useState(defaultLang === 'es' ? 'es' : 'en');
  const [busy, setBusy] = useState(false);
  const es = defaultLang === 'es';
  const T = {
    title: es ? 'Vista previa del informe' : 'Report Preview',
    notice: es
      ? 'Solo vista previa — nunca envía ni cambia el estado. El personal y los detalles internos se excluyen de la copia del Cliente.'
      : 'Preview only — never submits, sends, or changes status. Personnel and internal details are excluded from the Client copy.',
    audience: es ? 'Destinatario del informe' : 'Report audience',
    client: es ? 'Cliente' : 'Client',
    internal: es ? 'Interno' : 'Internal',
    language: es ? 'Idioma del informe' : 'Report language',
    langHint: es ? 'Toma el idioma del formulario · el inglés es el predeterminado de la app'
                 : 'Defaults to the form language · English is the app default',
    selected: es ? 'Seleccionado:' : 'Selected:',
    unreviewed: es ? '· traducción automática (sin revisar)' : '· automatic translation (unreviewed)',
    preview: es ? 'Vista previa PDF' : 'Preview PDF',
    download: es ? 'Descargar PDF' : 'Download PDF',
    saveErr: es ? 'No se pudieron guardar los últimos cambios — inténtelo de nuevo' : 'Could not save the latest edits — try again',
    dlErr: es ? 'Error al descargar' : 'Download failed',
  };
  const label = profile.inspection_number || profile.profile_number || '';
  const reqUrl = () => `${API}/operations/building-intel/${profile.id}/report.pdf?${audience === 'client' ? 'client=true&' : ''}lang=${lang}`;

  const ready = async () => {
    if (!onBeforeOpen) return true;
    setBusy(true);
    try { const ok = await onBeforeOpen(); return ok !== false; }
    catch { toast.error(T.saveErr); return false; }
    finally { setBusy(false); }
  };
  const doPreview = async () => { if (await ready()) window.open(`${reqUrl()}&auth=${token}`, '_blank'); };
  const doDownload = async () => {
    if (!(await ready())) return;
    try {
      const res = await axios.get(reqUrl(), { responseType: 'blob', headers: auth(token).headers });
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = blobUrl; a.download = `BI_${label || profile.id}_${audience}_${lang}.pdf`; a.click();
      URL.revokeObjectURL(blobUrl);
    } catch { toast.error(T.dlErr); }
  };
  const Pill = ({ active, onClick, children, tid }) => (
    <button data-testid={tid} onClick={onClick}
      className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${active ? 'bg-[#0E2A47] text-white' : 'border border-slate-300 text-[#0E2A47] hover:bg-slate-50'}`}>{children}</button>
  );
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="bi-report-dialog">
        <DialogHeader>
          <DialogTitle>{T.title}{label ? ` — ${label}` : ''}</DialogTitle>
          <DialogDescription>{profile.location_name} · {profile.customer_name}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="rounded-lg bg-slate-100 px-3 py-2 text-xs font-semibold text-slate-600"><Lock size={13} className="mr-1 inline" /> {T.notice}</div>
          <div>
            <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">{T.audience}</div>
            <div className="flex gap-2">
              <Pill tid="bi-aud-client" active={audience === 'client'} onClick={() => setAudience('client')}>{T.client}</Pill>
              <Pill tid="bi-aud-internal" active={audience === 'internal'} onClick={() => setAudience('internal')}>{T.internal}</Pill>
            </div>
          </div>
          <div>
            <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">{T.language}</div>
            <div className="flex items-center gap-2">
              <Pill tid="bi-lang-en" active={lang === 'en'} onClick={() => setLang('en')}>English</Pill>
              <Pill tid="bi-lang-es" active={lang === 'es'} onClick={() => setLang('es')}>Español</Pill>
              <span className="text-[11px] text-slate-400">{T.langHint}</span>
            </div>
          </div>
          <div className="rounded-lg border border-slate-200 px-3 py-2 text-xs text-slate-600" data-testid="bi-report-selection">{T.selected} <b>{audience === 'client' ? T.client : T.internal}</b> · <b>{lang === 'es' ? 'Español' : 'English'}</b>{lang === 'es' && <span className="ml-1 text-amber-600"> {T.unreviewed}</span>}</div>
          <div className="flex gap-2">
            <button onClick={doPreview} disabled={busy} data-testid="bi-report-preview" className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-[#0E2A47] px-3 py-2.5 text-sm font-semibold text-white hover:bg-[#0E2A47]/90 disabled:opacity-60"><Eye size={15} /> {T.preview}</button>
            <button onClick={doDownload} disabled={busy} data-testid="bi-report-download" className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-[#0E2A47] px-3 py-2.5 text-sm font-semibold text-[#0E2A47] hover:bg-[#0E2A47]/5 disabled:opacity-60"><Download size={15} /> {T.download}</button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BiReportDialog;
